import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Loader2, KeyRound, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    if (!email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        identifier: email.trim()
      });
      
      toast.success('OTP sent to your email!');
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join('');
    
    if (otpString.length !== 4) {
      toast.error('Please enter complete 4-digit OTP');
      return;
    }

    setIsVerifying(true);
    try {
      const response = await axios.post(`${API}/auth/verify-reset-otp`, null, {
        params: { identifier: email, otp: otpString }
      });
      
      toast.success('OTP verified successfully!');
      // Navigate to reset password page with email and OTP
      navigate('/reset-password', { state: { email, otp: otpString } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    setOtp(['', '', '', '']);
    await handleSendOTP({ preventDefault: () => {} });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center">
              <span className="text-2xl">🎓</span>
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Iqra Skills
            </h1>
          </div>
          <p className="text-slate-400">Reset your password</p>
        </div>

        {/* Card */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-8 shadow-2xl">
          {!otpSent ? (
            // Step 1: Enter Email
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <KeyRound className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">Forgot Password?</h2>
                  <p className="text-slate-400 text-sm">We'll send you a reset OTP</p>
                </div>
              </div>

              <form onSubmit={handleSendOTP} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-slate-300 mb-2 block">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your registered email"
                      className="pl-11 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 h-12 rounded-xl"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 rounded-xl font-semibold"
                  data-testid="send-otp-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" />
                      Send OTP
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            // Step 2: Enter OTP
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">Enter OTP</h2>
                  <p className="text-slate-400 text-sm">Sent to {email}</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* OTP Input Boxes */}
                <div className="flex justify-center gap-3">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ''))}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-14 h-14 text-center text-2xl font-bold bg-slate-700/50 border-2 border-slate-600 rounded-xl text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                      data-testid={`otp-input-${index}`}
                    />
                  ))}
                </div>

                {/* Timer & Resend */}
                <div className="text-center">
                  <p className="text-slate-400 text-sm mb-2">
                    Didn't receive the OTP?
                  </p>
                  <button
                    onClick={handleResendOTP}
                    disabled={isLoading}
                    className="text-cyan-400 hover:text-cyan-300 font-medium text-sm"
                  >
                    Resend OTP
                  </button>
                </div>

                <Button
                  onClick={handleVerifyOTP}
                  disabled={isVerifying || otp.join('').length !== 4}
                  className="w-full h-12 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 rounded-xl font-semibold"
                  data-testid="verify-otp-btn"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify OTP'
                  )}
                </Button>

                <button
                  onClick={() => { setOtpSent(false); setOtp(['', '', '', '']); }}
                  className="w-full text-slate-400 hover:text-white text-sm flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Change email address
                </button>
              </div>
            </>
          )}

          {/* Back to Login */}
          <div className="mt-6 pt-6 border-t border-slate-700">
            <Link
              to="/auth/login"
              className="flex items-center justify-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          © 2026 Iqra Skills Pvt Ltd. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
